import { useRef, useEffect } from "react";
import type { TrackedProcess, ProcessStep } from "@/providers/ProcessTrackerProvider";
import ProcessStepLineItem from "@/components/ProcessStepLineItem";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";

function resolveStepLabel(step: ProcessStep): string {
  if (step.status === "processing" && step.processingLabel) return step.processingLabel;
  if (step.status === "complete" && step.completedLabel) return step.completedLabel;
  if (step.status === "error" && step.errorLabel) return step.errorLabel;
  return step.label;
}

function isProcessDone(process: TrackedProcess): boolean {
  return process.steps.every((s) => s.status === "complete" || s.status === "error");
}

function getActiveStep(process: TrackedProcess) {
  const processing = process.steps.find((s) => s.status === "processing");
  if (processing) return processing;

  let lastDone: (typeof process.steps)[number] | undefined;
  for (const step of process.steps) {
    if (step.status === "complete" || step.status === "error") lastDone = step;
  }
  if (lastDone) return lastDone;

  return process.steps[0];
}

interface ProcessTrackerCardProps {
  process: TrackedProcess;
  onDismiss: (processId: string) => void;
  onToggleMinimized: (processId: string) => void;
}

export function ProcessTrackerCard({ process, onDismiss, onToggleMinimized }: ProcessTrackerCardProps) {
  const done = isProcessDone(process);
  const hasError = process.steps.some((s) => s.status === "error");
  const activeStep = getActiveStep(process);
  const stepsToShow = process.minimized && activeStep ? [activeStep] : process.steps;

  return (
    <div className="rounded-lg shadow-web-3 bg-sys-brand-surface w-[400px]" data-testid="process-tracker-card">
      <div className="flex items-center justify-between pl-6 pr-3">
        <span className="text-body-large">{process.title}</span>
        <IconButton
          name={process.minimized ? "keyboard_arrow_up" : "keyboard_arrow_down"}
          onClick={() => onToggleMinimized(process.processId)}
          title={process.minimized ? "Expand" : "Minimize"}
          data-testid="toggle-minimize"
          className="p-0"
        />
      </div>

      <div className="px-6 pb-5 gap-2 grid">
        {stepsToShow.map((step) => (
          <ProcessStepLineItem
            key={step.id}
            status={step.status}
            headline={resolveStepLabel(step)}
            supportingText={step.status === "error" ? step.errorMessage : undefined}
          />
        ))}
      </div>

      {(done || hasError) && (
        <div className="flex items-center justify-end gap-2 px-4 pb-4">
          <Button variant="text" onClick={() => onDismiss(process.processId)} testId="dismiss-process">
            Dismiss
          </Button>
          {done && !hasError && process.completionAction && (
            <a href={process.completionAction.to}>
              <Button variant="outlined" onClick={() => onDismiss(process.processId)} testId="completion-action">
                {process.completionAction.label}
              </Button>
            </a>
          )}
          {hasError && process.errorAction && (
            <a href={process.errorAction.to}>
              <Button variant="outlined" onClick={() => onDismiss(process.processId)} testId="error-action">
                {process.errorAction.label}
              </Button>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

interface ProcessTrackerStackProps {
  processes: TrackedProcess[];
  onDismiss: (processId: string) => void;
  onToggleMinimized: (processId: string) => void;
}

export function ProcessTrackerStack({ processes, onDismiss, onToggleMinimized }: ProcessTrackerStackProps) {
  const stackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      document.documentElement.style.setProperty("--process-tracker-height", `${height}px`);
      document.documentElement.style.setProperty("--process-tracker-gap", "12px");
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--process-tracker-height");
      document.documentElement.style.removeProperty("--process-tracker-gap");
    };
  }, []);

  return (
    <div
      ref={stackRef}
      className="fixed bottom-6 right-6 z-50 flex flex-col-reverse gap-3"
      data-testid="process-tracker-stack"
    >
      {processes.map((process) => (
        <ProcessTrackerCard
          key={process.processId}
          process={process}
          onDismiss={onDismiss}
          onToggleMinimized={onToggleMinimized}
        />
      ))}
    </div>
  );
}
